import { Icon } from "@iconify/react";

const industries = [
  ["solar:case-linear", "Consulting"],
  ["solar:palette-linear", "Agencies"],
  ["solar:users-group-rounded-linear", "Product teams"],
  ["solar:square-academic-cap-linear", "Training companies"],
  ["solar:hand-shake-linear", "Client services"],
] as const;

export function TrustBarSection() {
  return (
    <section className="border-y border-[#2B3624]/[0.06] py-8 sm:py-10">
      <div className="text-center">
        <p className="text-sm font-medium text-[#66705F]">Built for teams that work through conversations</p>
        <div className="mt-7 grid grid-cols-2 gap-4 text-sm font-medium text-[#7B8277] sm:grid-cols-5">
          {industries.map(([icon, label], i) => (
            <div
              key={label}
              className={`flex items-center justify-center gap-2 ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}
            >
              <Icon icon={icon} />
              {label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const problems = [
  {
    icon: "solar:videocamera-record-linear",
    title: "Meetings disappear",
    body: "Important decisions and context get lost after the call ends.",
  },
  {
    icon: "solar:folder-with-files-linear",
    title: "Knowledge stays fragmented",
    body: "Files, notes, and conversations live in separate tools.",
  },
  {
    icon: "solar:checklist-minimalistic-linear",
    title: "Follow-ups become manual",
    body: "Tasks and next steps depend on someone remembering to write them down.",
  },
  {
    icon: "solar:stars-minimalistic-linear",
    title: "AI lacks context",
    body: "Generic AI tools do not understand the full history of your project.",
  },
] as const;

export function ProblemSection() {
  return (
    <section className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <span className="text-xs font-medium uppercase tracking-widest text-[#7C8D52]">
            The collaboration gap
          </span>
          <h2 className="mt-4 text-3xl font-normal tracking-tight text-[#2B3624] sm:text-4xl lg:text-5xl">
            Your team already has the information. It is just scattered.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5F675B] sm:text-lg">
            Project knowledge gets buried across calls, chat threads, notes, files, and individual memory.
            Teams waste time searching, repeating discussions, and reconstructing decisions.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {problems.map((item) => (
            <article key={item.title} className="rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-6">
              <div className="mb-8 flex h-10 w-10 items-center justify-center rounded-full border border-[#2B3624]/10 bg-white/60 text-[#2B3624]">
                <Icon icon={item.icon} className="text-xl" />
              </div>
              <h3 className="text-lg font-medium text-[#2B3624]">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#66705F]">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const features = [
  "Real-time chat and calls",
  "Live meeting transcription",
  "Workspace memory",
  "AI-generated tasks and decisions",
  "Search project knowledge",
  "Flow canvas for processes",
  "Files, notes, and deliverables",
  "Workspace-specific AI",
] as const;

const featureIcons = [
  "solar:chat-round-call-linear",
  "solar:microphone-3-linear",
  "solar:database-linear",
  "solar:checklist-minimalistic-linear",
  "solar:magnifer-linear",
  "solar:branching-paths-down-linear",
  "solar:folder-with-files-linear",
  "solar:stars-minimalistic-linear",
] as const;

export function ProductSection() {
  return (
    <section
      id="product"
      className="-mx-5 bg-[#1D2718] px-5 py-14 text-[#F6F3EC] sm:-mx-8 sm:px-8 sm:py-20 lg:-mx-12 lg:px-12 lg:py-28 xl:-mx-16 xl:px-16"
    >
      <div className="mx-auto max-w-6xl">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-[#C9D68A]">
              One continuous project memory
            </span>
            <h2 className="mt-4 text-3xl font-normal tracking-tight sm:text-4xl lg:text-5xl">
              One workspace that listens, remembers, and organizes.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#D8D0B8] sm:text-lg">
              CollaboratorX creates a shared operating space for your team and workspace AI. Every
              conversation, document, meeting, task, and decision becomes part of one continuous project
              memory.
            </p>

            <div className="mt-9 grid gap-3 sm:grid-cols-2">
              {features.map((label, i) => (
                <div key={label} className="flex items-center gap-3 text-sm text-[#E9E5D9]">
                  <Icon icon={featureIcons[i]!} className="text-[#C9D68A]" />
                  {label}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 shadow-2xl">
            <div className="overflow-hidden rounded-xl border border-white/10 bg-[#F8F6F1] text-[#2B3624]">
              <div className="flex items-center justify-between border-b border-[#2B3624]/10 bg-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#2B3624] text-white">
                    <Icon icon="solar:stars-minimalistic-linear" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold">Workspace Intelligence</p>
                    <p className="text-xs text-[#8A9185]">Northstar Launch · Updated now</p>
                  </div>
                </div>
                <span className="rounded-full bg-[#E6EBCB] px-2 py-1 text-xs font-medium text-[#5D6C42]">
                  Live
                </span>
              </div>
              <div className="grid gap-3 p-4 sm:grid-cols-2">
                <div className="rounded-lg border border-[#2B3624]/10 bg-white p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-[#8A9185]">Project summary</p>
                  <p className="mt-3 text-sm leading-relaxed text-[#4B5563]">
                    Pilot launch moved to September. Campaign assets await client approval. Stakeholder review
                    is the next critical action.
                  </p>
                </div>
                <div className="rounded-lg border border-[#2B3624]/10 bg-white p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-[#8A9185]">Captured today</p>
                  <div className="mt-3 space-y-2 text-xs text-[#4B5563]">
                    <p className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#7C8D52]" />1 new decision
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#D6A27C]" />3 tasks assigned
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#B85C50]" />1 approval risk
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
